import {
  Component,
  inject,
  HostListener,
  OnInit,
  OnDestroy,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { A11yModule } from '@angular/cdk/a11y';
import { M3NavigationRailComponent } from '@shared/m3-navigation-rail/m3-navigation-rail.component';
import { M3NavigationRailItemComponent } from '@shared/m3-navigation-rail/m3-navigation-rail-item.component';
import { NavigationRailFooterComponent } from './components/navigation-rail-footer.component';
import { FilterSidebarComponent, FilterSidebarService } from '@shared/sidebar';
import { AppLayoutStore } from './state/app-layout.store';

import { UserStore } from '@core/user';
import { UserRightsStore } from '@core/user-rights';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TenantStore } from '@core/tenant';
import { EnumStore } from '@app/features/options/enum';

import { AgencyStore } from '@app/features/options/agency';
import { CaseStateStore } from '@app/features/options/case-state';
import { CaseTypeStore } from '@app/features/options/case-type';
import { TenantOptionStore } from '@app/features/options/tenant';
import { FeatureStore } from '@app/features/options/feature';
import { PrintTemplateOptionStore } from '@app/features/options/print-template';
import { TextBlockCombinationOptionStore } from '@app/features/options/text-block-combination';
import { InsuranceCompanyPreferenceStore } from '@app/features/options/insurance-company-preference';
import { CustomisationStore } from '@app/features/customisation/state/customisation.store';
import { ViewConfigStore } from '@app/features/support/view-configurator/state/view-config.store';
import { ViewLayoutStore } from '@app/features/support/view-configurator/state/view-layout.store';
import { ViewImportExportStore } from '@app/features/support/view-configurator/state/view-import-export.store';

import { VehicleViewStore } from '@app/features/view-loader/state/vehicle-view.store';
import { ValuationTaxStore } from '@app/features/valuation-request/state/valuation-tax.store';
import { TextCombinationStore } from '@app/features/text-combinations-list/state/text-combination.store';
import { CaseTypeListStore } from '@app/features/case-types-list/state/case-type.store';
import { TextBlockStore } from '@app/features/text-blocks-list/state/text-block.store';
import { TextBlockCategoryStore } from '@app/features/text-block-categories-list/state/text-block-category.store';
import { TenantSettingsStore } from '@app/features/tenant-settings/state/tenant-settings.store';
import { DefaultNavigationConfigStore } from '@app/features/support/navigation-config/state/default-navigation-config.store';
import { CurrentNavigationConfigStore } from '@app/features/support/navigation-config/state/current-navigation-config.store';
import { NavigationConfigStore } from '@app/features/support/navigation-config/state/navigation-config.store';
import { SuggestionStore } from '@app/features/options/suggestion';
import { CustomisationSalvageExchangeStore } from '@app/features/options/salvage-exchange-provider';
import {
  PlaceholderStore,
  PartialsPlaceholderStore,
} from '@app/features/options/placeholder';

@Component({
  selector: 'app-app-layout',
  standalone: true,
  providers: [
    UserStore,
    TenantStore,
    AppLayoutStore,
    EnumStore,

    AgencyStore,
    CaseStateStore,
    CaseTypeStore,
    TenantOptionStore,
    FeatureStore,
    PrintTemplateOptionStore,
    TextBlockCombinationOptionStore,
    InsuranceCompanyPreferenceStore,
    CustomisationStore,

    ViewConfigStore,
    ViewLayoutStore,
    ViewImportExportStore,

    VehicleViewStore,
    ValuationTaxStore,
    TextCombinationStore,
    CaseTypeListStore,
    TextBlockStore,
    TextBlockCategoryStore,
    TenantSettingsStore,
    DefaultNavigationConfigStore,
    CurrentNavigationConfigStore,
    NavigationConfigStore,
    SuggestionStore,
    CustomisationSalvageExchangeStore,
    PlaceholderStore,
    PartialsPlaceholderStore,
  ],
  imports: [
    CommonModule,
    RouterOutlet,
    A11yModule,
    M3NavigationRailComponent,
    M3NavigationRailItemComponent,
    NavigationRailFooterComponent,
    FilterSidebarComponent,
    MatProgressSpinnerModule,
  ],
  template: `
    @defer (when !isLoading()) {
      <div class="app-shell">
        @if (store.showSidebar() || store.railVariant() === 'modal') {
          <div
            class="nav-rail-container"
            [class.modal]="store.railVariant() === 'modal'"
            [cdkTrapFocus]="store.railVariant() === 'modal'"
            [cdkTrapFocusAutoCapture]="store.railVariant() === 'modal'"
          >
            <app-m3-navigation-rail
              [variant]="store.railVariant()"
              [orientation]="'vertical'"
              [hasHeaderContent]="true"
              (variantChange)="store.setRailVariant($event)"
              class="nav-rail"
            >
              @for (navItem of store.navigationRoutes(); track navItem.id) {
                <app-m3-navigation-rail-item
                  [label]="navItem.label"
                  [icon]="navItem.icon"
                  [routerLink]="navItem.route"
                  (click)="onNavItemClick()"
                ></app-m3-navigation-rail-item>
              }
            </app-m3-navigation-rail>

            <app-navigation-rail-footer />
          </div>
        }
        @if (store.railVariant() === 'modal') {
          <div
            class="modal-backdrop"
            (click)="closeModal()"
            (keydown.enter)="closeModal()"
            (keydown.space)="closeModal()"
            [attr.tabindex]="0"
            [attr.aria-label]="'Navigation schließen'"
            role="button"
          ></div>
        }

        <div class="main-area">
          <main class="main-content">
            <div class="outlet-container">
              <router-outlet />
            </div>
          </main>
        </div>

        <router-outlet name="dialog" />
        @if (filterSidebarService.state().isOpen) {
          <app-filter-sidebar />
        }
      </div>
    } @loading {
      <div class="loading-container">
        <mat-spinner diameter="48" />
        <span class="loading-text">{{ loadingText() }}</span>
      </div>
    } @placeholder {
      <div class="loading-container">
        <mat-spinner diameter="48" />
        <span class="loading-text">{{ loadingText() }}</span>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        height: 100vh;
        width: 100vw;
        background: var(--md-sys-color-surface);
      }

      .loading-text {
        color: var(--md-sys-color-on-surface-variant);
        font-size: 0.875rem;
      }

      .nav-rail-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        background-color: var(--md-sys-color-surface);
        transition: width var(--md-sys-motion-expressive-fast);
        overflow: visible;
      }

      .nav-rail-container.modal {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 1000;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
        animation: slideIn 200ms cubic-bezier(0.4, 0, 0.2, 1);
      }

      @keyframes slideIn {
        from {
          transform: translateX(-100%);
        }
        to {
          transform: translateX(0);
        }
      }

      .modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.4);
        z-index: 999;
        animation: fadeIn 200ms cubic-bezier(0.4, 0, 0.2, 1);
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      app-m3-navigation-rail {
        flex: 1;
        display: flex;
        background-color: var(--md-sys-color-surface);
        overflow: visible;
      }

      app-m3-navigation-rail ::ng-deep .menu-toggle:hover {
        background-color: var(--md-sys-color-on-surface);
        background-color: rgba(
          from var(--md-sys-color-on-surface) r g b / 0.08
        );
      }

      app-m3-navigation-rail ::ng-deep .menu-toggle:active {
        background-color: rgba(
          from var(--md-sys-color-on-surface) r g b / 0.12
        );
      }

      .app-shell {
        display: flex;
        height: 100dvh;
      }

      .nav-rail {
        flex-shrink: 0;
      }

      .main-area {
        display: flex;
        flex: 1 1 0%;
        flex-direction: column;
        overflow: hidden;
        transition-duration: 500ms;
        background: var(--md-sys-color-surface);
      }

      .main-content {
        flex: 1 1 0%;
        overflow: hidden;
        padding: 0;
      }

      .outlet-container {
        height: 100%;
        background: var(--md-sys-color-surface);
        overflow: hidden;
        box-sizing: border-box;
      }
    `,
  ],
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  store = inject(AppLayoutStore);
  filterSidebarService = inject(FilterSidebarService);
  private userStore = inject(UserStore);
  private userRightsStore = inject(UserRightsStore);
  private viewConfigStore = inject(ViewConfigStore);
  private tenantSettingsStore = inject(TenantSettingsStore);

  ngOnInit() {
    this.viewConfigStore.loadViews();
    this.tenantSettingsStore.loadSettings();
  }

  isLoading = computed(() => {
    return (
      this.userStore.isCurrentUserLoading() ||
      this.userRightsStore.isGetUserRightsLoading() ||
      !this.viewConfigStore.isLoadViewsLoaded()
    );
  });

  loadingText = computed(() => {
    if (this.userStore.isCurrentUserLoading()) {
      return 'Benutzer wird geladen...';
    }
    if (this.userRightsStore.isGetUserRightsLoading()) {
      return 'Berechtigungen werden geladen...';
    }
    if (!this.viewConfigStore.isLoadViewsLoaded()) {
      return 'Ansichten werden geladen...';
    }
    return '';
  });

  ngOnDestroy(): void {
    console.log('destroy');
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.store.railVariant() === 'modal') {
      this.closeModal();
    }
  }

  closeModal() {
    this.store.setRailVariant('collapsed');
  }

  // Path changes close the modal rail in the store; this covers the entry of the current page
  onNavItemClick() {
    if (this.store.railVariant() === 'modal') {
      this.closeModal();
    }
  }
}
